import React, { useState, useCallback } from "react";
import styled from "styled-components";
import Cropper from "react-easy-crop";
import imageCompression from "browser-image-compression";

import { P3 } from "./theme/text/Headings";
import { Black, Blue, DarkGrey, White05, White08 } from "./theme/colors";

const CropOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${Black};
  z-index: 2000;
  display: flex;
  flex-direction: column;
`;

const CropInner = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 12px 24px calc(env(safe-area-inset-bottom, 0px) + 12vh);
  box-sizing: border-box;
  max-width: 520px;
  width: 100%;
  margin: 0 auto;
`;

const CropContent = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  background: #000;
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
`;

const CropActions = styled.div`
  padding: 0 0 8px;
  display: flex;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
`;

const CropButton = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: ${(props) => (props.$primary ? `${Blue}` : DarkGrey)};
`;

const SliderRow = styled.div`
  padding: 0px 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${White05};
`;

const SliderInput = styled.input`
  flex: 1;
`;

const DefaultLabel = styled.label`
  display: inline-block;
  padding: 7px 20px;
  background: ${DarkGrey};
  color: ${White08};
  border-radius: 200px;
  cursor: pointer;
  margin-top: 20px;
  text-align: center;
`;

/**
 * PhotoCropperInput - A reusable component for selecting and cropping photos
 *
 * @param {Object} props
 * @param {Function} props.onPhotoReady - Callback with (file, previewUrl) when cropping is complete
 * @param {string} props.inputId - Unique ID for the file input (default: "photo-crop-input")
 * @param {React.ReactNode} props.children - Custom trigger element (optional, defaults to "Choose File" button)
 * @param {Object} props.labelStyle - Additional styles for the default label (optional)
 * @param {Object} props.compressionOptions - Options for image compression (optional)
 */
const PhotoCropperInput = ({
  onPhotoReady,
  inputId = "photo-crop-input",
  children,
  labelStyle = {},
  compressionOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1080,
    useWebWorker: true,
  },
}) => {
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropping, setCropping] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const getCroppedImg = useCallback(async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.92
      );
    });
  }, []);

  const handleCropConfirm = async () => {
    if (!cropSrc || !croppedAreaPixels) return;
    try {
      setCropping(true);
      const croppedBlob = await getCroppedImg(cropSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], "cropped.jpg", {
        type: "image/jpeg",
      });

      const compressedFile = await imageCompression(
        croppedFile,
        compressionOptions
      );
      const previewUrl = URL.createObjectURL(compressedFile);

      onPhotoReady(compressedFile, previewUrl);
    } catch (error) {
      console.error("Crop failed:", error);
      alert("There was an error cropping the image.");
    } finally {
      setCropping(false);
      if (cropSrc) URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    }
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Only image files are allowed.");
        return;
      }

      try {
        const objectUrl = URL.createObjectURL(file);
        setCropSrc(objectUrl);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
      } catch (error) {
        console.error("File handling failed:", error);
        alert("There was an error loading the image.");
      }
    }
    // Reset the input so the same file can be selected again
    e.target.value = "";
  };

  return (
    <>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      {children ? (
        <label htmlFor={inputId} style={{ cursor: "pointer" }}>
          {children}
        </label>
      ) : (
        <DefaultLabel htmlFor={inputId} style={labelStyle}>
          <P3>Choose File</P3>
        </DefaultLabel>
      )}

      {cropSrc && (
        <CropOverlay>
          <CropInner>
            <CropContent>
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="contain"
                style={{
                  containerStyle: { background: "#000" },
                }}
              />
            </CropContent>
            <SliderRow>
              <span style={{ minWidth: 40 }}>Zoom</span>
              <SliderInput
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </SliderRow>
            <CropActions>
              <CropButton onClick={handleCropCancel}>Cancel</CropButton>
              <CropButton
                $primary
                onClick={handleCropConfirm}
                disabled={cropping}
              >
                {cropping ? "Cropping..." : "Crop & Save"}
              </CropButton>
            </CropActions>
          </CropInner>
        </CropOverlay>
      )}
    </>
  );
};

export default PhotoCropperInput;
